import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useParams, Link, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { getGetLocationTwinQueryOptions, getGetTwinSceneQueryOptions } from '@cdf/api-client-react';
import { ArrowLeftIcon, MapIcon, BoxIcon, AlertCircleIcon, LayersIcon, MapPinnedIcon, Minimize2Icon, XIcon } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { TwinScene, isWebGLAvailable, TwinLegend, type SelectedItem, type TwinCameraController } from '@/components/twin/twin-scene';
import { ZoomPanControls } from '@/components/ui/zoom-pan-controls';
import {
  ALL_PEDESTRIAN_ROLES,
  PedestrianSimulation,
  createPedestrian,
  type PedestrianState,
  type PedestrianMeta,
  type PedestrianRole,
  type WalkBounds,
} from '@/components/twin/pedestrian-simulation';
import { PedestrianControls } from '@/components/twin/pedestrian-controls';
import { TwinAvatarCustomizer } from '@/components/twin/twin-avatar-customizer';
import { useLivePresencePedestrians } from '@/components/twin/use-live-presence-pedestrians';
import { useTwinPresence } from '@/hooks/use-twin-presence';
import { useFullscreenTwin } from '@/hooks/use-fullscreen-twin';
import { STATUS_LABELS, STATUS_COLORS, projectLngLat, type GeoJsonPolygon } from '@/lib/geo-projection';
import { useGetCivicRegions, type TwinAsset, type TwinIssue, type RegionFeatureCollection } from '@cdf/api-client-react';

const MOBILE_VIEWPORT_QUERY = '(max-width: 639px), (max-width: 935px) and (orientation: landscape), (max-height: 500px)';

function isMobileViewport(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(MOBILE_VIEWPORT_QUERY).matches;
}

export function DigitalTwinPage() {
  const { regionId, locationSlug } = useParams<{ regionId: string; locationSlug: string }>();
  const navigate = useNavigate();
  const isLocationTwin = Boolean(locationSlug);
  const [selected, setSelected] = useState<SelectedItem | null>(null);
  const [showBoundary, setShowBoundary] = useState(true);
  const [showBuildings, setShowBuildings] = useState(true);
  const [showAssets, setShowAssets] = useState(true);
  const [showIssues, setShowIssues] = useState(true);
  const [isMobile, setIsMobile] = useState(isMobileViewport);
  const webglOk = useMemo(() => isWebGLAvailable(), []);

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_VIEWPORT_QUERY);
    const updateIsMobile = () => setIsMobile(mediaQuery.matches);
    updateIsMobile();
    mediaQuery.addEventListener('change', updateIsMobile);
    return () => mediaQuery.removeEventListener('change', updateIsMobile);
  }, []);

  // ── Pedestrian simulation state ───────────────────────────────────
  const [pedEnabled, setPedEnabled] = useState(true);
  const [pedSpeed, setPedSpeed] = useState(1);
  const [showPaths, setShowPaths] = useState(false);
  const [visiblePedestrianRoles, setVisiblePedestrianRoles] = useState<PedestrianRole[]>(
    () => [...ALL_PEDESTRIAN_ROLES],
  );
  const [pedCount, setPedCount] = useState(0);
  const [selectedPed, setSelectedPed] = useState<PedestrianMeta | null>(null);
  const pedestriansRef = useRef<PedestrianState[]>([]);
  const cameraRef = useRef<TwinCameraController>(null);
  const {
    containerRef: fullscreenContainerRef,
    isFallbackFullscreen,
    isFullscreen,
    isLandscape,
    toggleFullscreen,
    exitFullscreen,
  } = useFullscreenTwin();

  useEffect(() => {
    if (selectedPed && !visiblePedestrianRoles.includes(selectedPed.role)) {
      setSelectedPed(null);
    }
  }, [selectedPed, visiblePedestrianRoles]);

  useEffect(() => {
    if (selected?.kind === 'asset' && !showAssets) setSelected(null);
    if (selected?.kind === 'issue' && !showIssues) setSelected(null);
  }, [selected, showAssets, showIssues]);

  const locationTwinQuery = useQuery({
    ...getGetLocationTwinQueryOptions(locationSlug ?? ''),
    enabled: isLocationTwin,
  });
  const regionTwinQuery = useQuery({
    ...getGetTwinSceneQueryOptions(regionId ?? ''),
    enabled: !isLocationTwin && Boolean(regionId),
  });
  const data = isLocationTwin ? locationTwinQuery.data?.scene : regionTwinQuery.data;
  const isLoading = isLocationTwin ? locationTwinQuery.isLoading : regionTwinQuery.isLoading;
  const error = isLocationTwin ? locationTwinQuery.error : regionTwinQuery.error;
  const twinTitle = isLocationTwin
    ? (locationTwinQuery.data?.twin.title ?? 'Location digital twin')
    : `${data?.region.name ?? 'Digital Twin'} · Digital Twin`;
  const twinSummary = isLocationTwin ? locationTwinQuery.data?.twin.summary : null;
  const presenceSceneId = `civic:${isLocationTwin ? `location:${locationSlug ?? 'pending'}` : `region:${regionId ?? 'pending'}`}`;
  const presence = useTwinPresence({
    sceneId: presenceSceneId,
    projectLocation: (location) => ({
      latitude: location.latitude,
      longitude: location.longitude,
      accuracyMeters: location.accuracyMeters,
    }),
  });

  const { data: regionsData } = useGetCivicRegions();
  const regionList = (regionsData as RegionFeatureCollection | undefined)?.features ?? [];

  // Compute walk bounds from the projected region boundary
  const civicWalkBounds = useMemo<WalkBounds | null>(() => {
    if (!data) return null;
    const boundary = data.region.boundary as unknown as GeoJsonPolygon;
    const coords = boundary.coordinates?.[0];
    if (!coords || coords.length === 0) return null;
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
    for (const coord of coords) {
      const [x, z] = projectLngLat(coord[0]!, coord[1]!, data.region.centerLng, data.region.centerLat);
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (z < minZ) minZ = z;
      if (z > maxZ) maxZ = z;
    }
    // Inset bounds slightly so pedestrians stay inside the region
    return { minX: minX + 5, maxX: maxX - 5, minZ: minZ + 5, maxZ: maxZ - 5 };
  }, [data]);

  const projectCivicPresence = useCallback((participant: { latitude?: number | null; longitude?: number | null; floorX?: number | null; floorZ?: number | null }) => {
    if (participant.longitude != null && participant.latitude != null && data) {
      const [x, z] = projectLngLat(participant.longitude, participant.latitude, data.region.centerLng, data.region.centerLat);
      return new THREE.Vector3(x, 0, z);
    }
    if (participant.floorX != null && participant.floorZ != null) return new THREE.Vector3(participant.floorX, 0, participant.floorZ);
    return null;
  }, [data]);
  const ownLivePresence = useMemo(() => {
    if (!presence.isSharing || !presence.locationTrack.location) return null;
    const location = presence.locationTrack.location;
    return {
      userId: `local:${presenceSceneId}`,
      displayName: presence.displayName,
      avatarColor: presence.avatarColor,
      accuracyMeters: location.accuracyMeters,
      position: projectCivicPresence({ latitude: location.latitude, longitude: location.longitude }),
    };
  }, [presence.avatarColor, presence.displayName, presence.isSharing, presence.locationTrack.location, presenceSceneId, projectCivicPresence]);
  const livePedestrians = useLivePresencePedestrians(presence.participants, projectCivicPresence, ownLivePresence);

  const handleSpawnPedestrian = useCallback(() => {
    const bounds = civicWalkBounds;
    if (!bounds) return;
    const ped = createPedestrian(bounds);
    pedestriansRef.current = [...pedestriansRef.current, ped];
    setPedCount(pedestriansRef.current.length);
  }, [civicWalkBounds]);

  const handleResetPedestrians = useCallback(() => {
    pedestriansRef.current = [];
    setPedCount(0);
    setSelectedPed(null);
  }, []);

  const handleSelectPedestrian = useCallback((meta: PedestrianMeta) => {
    setSelectedPed(meta);
  }, []);

  // ── Loading ─────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex h-[calc(100vh-4rem)] flex-col">
        <PageHeader title={isLocationTwin ? 'Location Digital Twin' : 'Digital Twin'} icon={BoxIcon} />
        <div className="flex-1 flex items-center justify-center">
          <Skeleton className="h-full w-full" />
        </div>
      </div>
    );
  }

  // ── Error ───────────────────────────────────────────────────────────
  if (error || !data) {
    return (
      <div className="flex h-[calc(100vh-4rem)] flex-col">
        <PageHeader title={isLocationTwin ? 'Location Digital Twin' : 'Digital Twin'} icon={BoxIcon} />
        <div className="flex-1 flex items-center justify-center">
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircleIcon className="h-5 w-5 text-destructive" />
                Unable to load 3D scene
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                {error instanceof Error
                  ? error.message
                  : isLocationTwin
                    ? 'The location scene could not be loaded.'
                    : 'The region data could not be loaded.'}
              </p>
              <Button asChild variant="outline">
                <Link to="/map"><MapIcon className="mr-2 h-4 w-4" /> Back to Map</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ── WebGL fallback ──────────────────────────────────────────────────
  if (!webglOk) {
    return (
      <div className="flex h-[calc(100vh-4rem)] flex-col">
        <PageHeader title={isLocationTwin ? 'Location Digital Twin' : 'Digital Twin'} icon={BoxIcon} />
        <div className="flex-1 flex items-center justify-center">
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircleIcon className="h-5 w-5 text-amber-500" />
                3D not available
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Your browser doesn't support WebGL, which is required for the 3D digital twin view.
                You can still explore the area on the 2D map.
              </p>
              <Button asChild>
                <Link to="/map"><MapIcon className="mr-2 h-4 w-4" /> Open 2D Map</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ── 3D scene ────────────────────────────────────────────────────────
  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col">
      {/* Header with region selector */}
      <div className="flex items-center gap-3 border-b border-border px-4 py-2 bg-background/95 backdrop-blur z-20">
        <Button asChild variant="ghost" size="sm">
          <Link to="/map"><ArrowLeftIcon className="h-4 w-4" /></Link>
        </Button>
        <h1 className="text-sm font-semibold truncate">
          {twinTitle}
        </h1>
        <span className="text-xs text-muted-foreground">
          {data.buildings.length} buildings · {data.assets.length} assets · {data.issues.length} issues
        </span>
        <div className="ml-auto flex items-center gap-2">
          {!isLocationTwin && regionList.length > 0 && (
            <Select
              value={regionId}
              onValueChange={(value) => navigate(`/twin/${value}`)}
            >
              <SelectTrigger className="h-8 w-48 text-xs">
                <SelectValue placeholder="Switch region" />
              </SelectTrigger>
              <SelectContent>
                {regionList.map((r) => (
                  <SelectItem key={r.properties?.id ?? ''} value={r.properties?.id ?? ''}>{r.properties?.name ?? 'Unknown'}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <TwinAvatarCustomizer sceneLabel={twinTitle} presence={presence} />
          <Button asChild variant="ghost" size="sm">
            <Link to="/map"><MapIcon className="mr-1 h-3.5 w-3.5" /> 2D Map</Link>
          </Button>
        </div>
      </div>

      {/* 3D canvas */}
      <div
        ref={fullscreenContainerRef}
        className={
          isFallbackFullscreen
            ? 'fixed inset-0 z-[60] h-[100dvh] w-screen bg-background'
            : isFullscreen
              ? 'relative h-full w-full bg-background'
              : 'relative flex-1'
        }
      >
        {isFullscreen && isLandscape && (
          <div className="absolute inset-x-0 top-0 z-50 flex min-h-14 items-center justify-between gap-3 border-b bg-background/95 px-3 py-1.5 backdrop-blur supports-[backdrop-filter]:bg-background/80">
            <p className="min-w-0 truncate text-sm font-semibold">{twinTitle}</p>
            <div className="flex shrink-0 items-center gap-2">
              <TwinAvatarCustomizer sceneLabel={twinTitle} presence={presence} />
              <Button
                type="button"
                size="sm"
                variant="secondary"
                className="h-10 gap-1.5"
                aria-label="Exit full screen"
                onClick={() => void exitFullscreen()}
              >
                <Minimize2Icon className="size-4" aria-hidden="true" />
                <span className="hidden sm:inline">Exit</span>
              </Button>
            </div>
          </div>
        )}
        <TwinScene
          data={data}
          onSelect={setSelected}
          selectedId={selected?.id ?? null}
          showBoundary={showBoundary}
          showBuildings={showBuildings}
          showAssets={showAssets}
          showIssues={showIssues}
          ref={cameraRef}
        >
          {civicWalkBounds && livePedestrians.length > 0 && (
            <PedestrianSimulation
              pedestrians={livePedestrians}
              bounds={civicWalkBounds}
              visibleRoles={ALL_PEDESTRIAN_ROLES}
              selectedId={selectedPed?.id}
              onSelect={handleSelectPedestrian}
            />
          )}
          {pedEnabled && civicWalkBounds && (
            <PedestrianSimulation
              pedestrians={pedestriansRef.current}
              bounds={civicWalkBounds}
              showPaths={showPaths}
              visibleRoles={visiblePedestrianRoles}
              selectedId={selectedPed?.id}
              onSelect={handleSelectPedestrian}
              speedMultiplier={pedSpeed}
            />
          )}
        </TwinScene>
        <TwinLegend />
        <ZoomPanControls
          onZoomIn={() => cameraRef.current?.zoomIn()}
          onZoomOut={() => cameraRef.current?.zoomOut()}
          onPan={(direction) => cameraRef.current?.pan(direction)}
          onReset={() => cameraRef.current?.reset()}
          onToggleFullscreen={() => void toggleFullscreen()}
          isFullscreen={isFullscreen}
          className={isFullscreen && isLandscape ? 'bottom-4 right-4 sm:bottom-4' : 'bottom-20 sm:bottom-28'}
        />

        {isLocationTwin && (
          <>
            {!isMobile && (
              <Card className={isFullscreen && isLandscape ? 'absolute left-4 top-16 z-30 w-72 shadow-xl' : 'absolute left-4 top-4 z-30 w-72 shadow-xl'}>
                <LocationLayersContent
                  regionName={data.region.name}
                  summary={twinSummary}
                  buildingCount={data.buildings.length}
                  assetCount={data.assets.length}
                  issueCount={data.issues.length}
                  showBoundary={showBoundary}
                  onShowBoundaryChange={setShowBoundary}
                  showBuildings={showBuildings}
                  onShowBuildingsChange={setShowBuildings}
                  showAssets={showAssets}
                  onShowAssetsChange={setShowAssets}
                  showIssues={showIssues}
                  onShowIssuesChange={setShowIssues}
                  idPrefix="desktop-"
                />
              </Card>
            )}
            {isMobile && (
              <Sheet>
                <div className={isFullscreen && isLandscape ? 'absolute left-4 top-16 z-30' : 'absolute left-4 top-4 z-30'} onPointerDown={(event) => event.stopPropagation()}>
                  <SheetTrigger asChild>
                    <Button type="button" size="icon" variant="secondary" className="size-11 rounded-xl border bg-card/95 shadow-lg backdrop-blur" aria-label="Open property layers">
                      <LayersIcon className="size-5" aria-hidden />
                    </Button>
                  </SheetTrigger>
                </div>
                <SheetContent side="bottom" className={isFullscreen && isLandscape ? 'max-h-[calc(100dvh-4rem)] overflow-y-auto rounded-t-2xl px-4 pb-[calc(1rem+env(safe-area-inset-bottom))]' : 'max-h-[80dvh] overflow-y-auto rounded-t-2xl px-4 pb-[calc(1rem+env(safe-area-inset-bottom))]'} onPointerDown={(event) => event.stopPropagation()}>
                  <SheetHeader>
                    <SheetTitle>Property layers</SheetTitle>
                    <SheetDescription>Choose what is visible in this location digital twin.</SheetDescription>
                  </SheetHeader>
                  <div className="mt-5">
                    <LocationLayersContent
                      regionName={data.region.name}
                      summary={twinSummary}
                      buildingCount={data.buildings.length}
                      assetCount={data.assets.length}
                      issueCount={data.issues.length}
                      showBoundary={showBoundary}
                      onShowBoundaryChange={setShowBoundary}
                      showBuildings={showBuildings}
                      onShowBuildingsChange={setShowBuildings}
                      showAssets={showAssets}
                      onShowAssetsChange={setShowAssets}
                      showIssues={showIssues}
                      onShowIssuesChange={setShowIssues}
                      idPrefix="mobile-"
                      compact
                    />
                  </div>
                </SheetContent>
              </Sheet>
            )}
          </>
        )}

        {/* Pedestrian controls overlay */}
        <PedestrianControls
          enabled={pedEnabled}
          onToggle={setPedEnabled}
          count={pedCount}
          onSpawn={handleSpawnPedestrian}
          onReset={handleResetPedestrians}
          speed={pedSpeed}
          onSpeedChange={setPedSpeed}
          showPaths={showPaths}
          onShowPathsChange={setShowPaths}
          visibleRoles={visiblePedestrianRoles}
          onVisibleRolesChange={setVisiblePedestrianRoles}
          selectedPedestrian={selectedPed}
          onDeselectPedestrian={() => setSelectedPed(null)}
          className={isFullscreen && isLandscape ? 'top-16' : undefined}
          mobileMode={isMobile}
        />

        {/* Selected asset or issue */}
        {selected && (
          <>
            {!isMobile && (
              <div className={isFullscreen && isLandscape ? 'absolute right-4 top-16 z-40 w-80 max-h-[calc(100%-5rem)] overflow-auto' : 'absolute right-4 top-4 z-40 w-80 max-h-[calc(100%-2rem)] overflow-auto'}>
                <Card className="shadow-xl">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <CardTitle className="text-sm"><SelectedItemTitle selected={selected} /></CardTitle>
                    <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => setSelected(null)}>
                      <XIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3 text-sm"><SelectedItemDetails selected={selected} /></CardContent>
                </Card>
              </div>
            )}
            {isMobile && (
              <Sheet open onOpenChange={(open) => { if (!open) setSelected(null); }}>
                <SheetContent side="bottom" className={isFullscreen && isLandscape ? 'max-h-[calc(100dvh-4rem)] overflow-y-auto rounded-t-2xl px-4 pb-[calc(1rem+env(safe-area-inset-bottom))]' : 'max-h-[80dvh] overflow-y-auto rounded-t-2xl px-4 pb-[calc(1rem+env(safe-area-inset-bottom))]'} onPointerDown={(event) => event.stopPropagation()}>
                  <SheetHeader>
                    <SheetTitle><SelectedItemTitle selected={selected} /></SheetTitle>
                    <SheetDescription>Details for the selected {selected.kind}.</SheetDescription>
                  </SheetHeader>
                  <div className="mt-5 space-y-3 text-sm"><SelectedItemDetails selected={selected} /></div>
                </SheetContent>
              </Sheet>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Detail panels ─────────────────────────────────────────────────────────

interface LocationLayersContentProps {
  regionName: string;
  summary: string | null | undefined;
  buildingCount: number;
  assetCount: number;
  issueCount: number;
  showBoundary: boolean;
  onShowBoundaryChange: (checked: boolean) => void;
  showBuildings: boolean;
  onShowBuildingsChange: (checked: boolean) => void;
  showAssets: boolean;
  onShowAssetsChange: (checked: boolean) => void;
  showIssues: boolean;
  onShowIssuesChange: (checked: boolean) => void;
  compact?: boolean;
  idPrefix?: string;
}

function LocationLayersContent({
  regionName,
  summary,
  buildingCount,
  assetCount,
  issueCount,
  showBoundary,
  onShowBoundaryChange,
  showBuildings,
  onShowBuildingsChange,
  showAssets,
  onShowAssetsChange,
  showIssues,
  onShowIssuesChange,
  compact = false,
  idPrefix = '',
}: LocationLayersContentProps) {
  return (
    <>
      <CardHeader className="gap-1 pb-3">
        <CardTitle className="flex items-center gap-2 text-sm">
          <MapPinnedIcon className="size-4 text-primary" aria-hidden />
          {regionName}
        </CardTitle>
        {summary && <p className="text-xs leading-relaxed text-muted-foreground">{summary}</p>}
        <div className="flex flex-wrap gap-1.5 pt-1">
          <Badge variant="secondary">{buildingCount} buildings</Badge>
          <Badge variant="secondary">{assetCount} assets</Badge>
          <Badge variant="secondary">{issueCount} issues</Badge>
        </div>
      </CardHeader>
      <CardContent className={compact ? 'space-y-2 px-0 pt-3' : 'space-y-2 border-t pt-3'}>
        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          <LayersIcon className="size-3.5" aria-hidden /> Property layers
        </div>
        <LocationLayerToggle id={`${idPrefix}boundary`} label="Boundary and roads" checked={showBoundary} onCheckedChange={onShowBoundaryChange} />
        <LocationLayerToggle id={`${idPrefix}buildings`} label="3D buildings" checked={showBuildings} onCheckedChange={onShowBuildingsChange} />
        <LocationLayerToggle id={`${idPrefix}assets`} label="Assets" checked={showAssets} onCheckedChange={onShowAssetsChange} />
        <LocationLayerToggle id={`${idPrefix}issues`} label="Reported issues" checked={showIssues} onCheckedChange={onShowIssuesChange} />
      </CardContent>
    </>
  );
}

interface LocationLayerToggleProps {
  id: string;
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}

function LocationLayerToggle({ id, label, checked, onCheckedChange }: LocationLayerToggleProps) {
  const controlId = `location-twin-layer-${id}`;

  return (
    <div className="flex items-center gap-2">
      <Label htmlFor={controlId} className="cursor-pointer text-xs">{label}</Label>
      <Switch
        id={controlId}
        checked={checked}
        onCheckedChange={onCheckedChange}
        className="ml-auto"
      />
    </div>
  );
}

function SelectedItemTitle({ selected }: { selected: SelectedItem }) {
  return selected.kind === 'asset'
    ? (selected.data as { assetTypeName: string }).assetTypeName
    : (selected.data as { categoryName: string }).categoryName;
}

function SelectedItemDetails({ selected }: { selected: SelectedItem }) {
  return selected.kind === 'asset'
    ? <AssetDetail item={selected.data as TwinAsset} />
    : <IssueDetail item={selected.data as TwinIssue} />;
}

function AssetDetail({ item }: { item: { assetTypeName: string; assetTypeSlug: string; tenantName: string; tenantType: string; condition: string; address?: string; openIssueCount: number; id: string } }) {
  return (
    <>
      <div className="space-y-1.5">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Type</span>
          <span className="font-medium capitalize">{item.assetTypeSlug.replace(/_/g, ' ')}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Owner</span>
          <span className="font-medium">{item.tenantName}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Owner type</span>
          <Badge variant={item.tenantType === 'council' ? 'default' : 'secondary'}>
            {item.tenantType === 'council' ? 'Council' : 'Service Provider'}
          </Badge>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Condition</span>
          <Badge variant={
            item.condition === 'good' ? 'default' :
            item.condition === 'fair' ? 'secondary' :
            'destructive'
          }>{item.condition}</Badge>
        </div>
        {item.address && (
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground shrink-0">Address</span>
            <span className="text-right text-xs">{item.address}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-muted-foreground">Open issues</span>
          <span className="font-medium">{item.openIssueCount}</span>
        </div>
      </div>
    </>
  );
}

function IssueDetail({ item }: { item: { categoryName: string; categorySlug: string; status: string; severity: string; trackingToken: string; addressText?: string; tenantName: string; hasPhoto?: boolean; photoUrl?: string | null; createdAt: string; id: string } }) {
  return (
    <>
      {item.photoUrl && (
        <img
          src={item.photoUrl}
          alt="Issue"
          className="w-full rounded-md object-cover max-h-40"
        />
      )}
      <div className="space-y-1.5">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Category</span>
          <span className="font-medium capitalize">{item.categorySlug.replace(/_/g, ' ')}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Status</span>
          <span
            className="inline-flex items-center gap-1.5 font-medium"
          >
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ backgroundColor: STATUS_COLORS[item.status] ?? '#64748b' }}
            />
            {STATUS_LABELS[item.status] ?? item.status}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Severity</span>
          <Badge variant={item.severity === 'urgent' || item.severity === 'high' ? 'destructive' : 'secondary'}>
            {item.severity}
          </Badge>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Authority</span>
          <span className="font-medium">{item.tenantName}</span>
        </div>
        {item.addressText && (
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground shrink-0">Location</span>
            <span className="text-right text-xs">{item.addressText}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-muted-foreground">Reported</span>
          <span className="text-xs">{new Date(item.createdAt).toLocaleDateString()}</span>
        </div>
      </div>
      <Button asChild size="sm" className="w-full">
        <Link to={`/track/${item.trackingToken}`}>View Tracking Page</Link>
      </Button>
    </>
  );
}
